<template>
	<teleport to="body">
	<div v-if="isActive" class="onboarding" aria-live="polite">
		<div
			class="onboarding__overlay"
			:class="{ 'onboarding__overlay--with-spotlight': currentStep && !isMobile && hasValidTarget }"
			@click.stop
		></div>

		<div
			v-if="currentStep && !isMobile && hasValidTarget"
			class="onboarding__spotlight"
			:style="spotlightStyle"
		></div>

		<div
			v-if="currentStep"
			class="onboarding__tooltip"
			:style="tooltipStyle"
			:class="{ 'onboarding__tooltip--mobile': isMobile || !hasValidTarget }"
		>
			<div class="onboarding__tooltip-header">
				<h3 class="onboarding__tooltip-title">{{ currentStep.title }}</h3>
				<div class="onboarding__tooltip-header-controls">
					<div class="onboarding__tooltip-step">
						{{ currentStepIndex + 1 }} / {{ steps.length }}
					</div>
					<button
						type="button"
						class="onboarding__tooltip-close"
						aria-label="Закрыть обучение"
						@click="skipTour"
					>
						<span aria-hidden="true">&times;</span>
					</button>
				</div>
			</div>
			<div v-if="currentStep.hintImage" class="onboarding__tooltip-image-wrap">
				<img
					:src="currentStep.hintImage"
					:alt="currentStep.title"
					class="onboarding__tooltip-image"
					:style="hintImageStyle"
				/>
			</div>
			<p class="onboarding__tooltip-text">{{ currentStep.text }}</p>

			<div class="onboarding__tooltip-footer">
				<div class="onboarding__tooltip-progress">
					<span
						v-for="(step, index) in steps"
						:key="index"
						class="onboarding__tooltip-dot"
						:class="{ 'onboarding__tooltip-dot--active': index === currentStepIndex }"
					></span>
				</div>

				<div class="onboarding__tooltip-actions">
					<button
						type="button"
						class="onboarding__tooltip-btn onboarding__tooltip-btn--ghost"
						@click="skipTour"
					>
						Пропустить
					</button>
					<button
						type="button"
						class="onboarding__tooltip-btn onboarding__tooltip-btn--secondary"
						@click="restartTour"
					>
						Повторить
					</button>
					<button
						v-if="currentStepIndex > 0"
						type="button"
						class="onboarding__tooltip-btn onboarding__tooltip-btn--secondary"
						@click="previousStep"
					>
						Назад
					</button>
					<button
						v-if="currentStepIndex < steps.length - 1"
						type="button"
						class="onboarding__tooltip-btn onboarding__tooltip-btn--primary"
						@click="nextStep"
					>
						Далее
					</button>
					<button
						v-else
						type="button"
						class="onboarding__tooltip-btn onboarding__tooltip-btn--primary"
						@click="completeTour"
					>
						Завершить
					</button>
				</div>
			</div>
		</div>
	</div>
	</teleport>
</template>

<script>
export default {
	name: 'OnboardingTour',
	props: {
		steps: {
			type: Array,
			required: true,
		},
		autoStart: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			isActive: false,
			currentStepIndex: 0,
			spotlightStyle: {},
			tooltipStyle: {},
			resizeHandler: null,
			preventEscHandler: null,
			currentTargetElement: null,
			previousHtmlOverflow: '',
			previousBodyOverflow: '',
		}
	},
	computed: {
		currentStep() {
			return this.steps[this.currentStepIndex] || null
		},
		hintImageStyle() {
			const step = this.currentStep || {}
			const style = {}
			if (Number.isFinite(step.hintImageMaxWidth)) {
				style.maxWidth = `${step.hintImageMaxWidth}px`
			}
			if (Number.isFinite(step.hintImageMaxHeight)) {
				style.maxHeight = `${step.hintImageMaxHeight}px`
			}
			return style
		},
		isMobile() {
			if (typeof window === 'undefined') return false
			return window.innerWidth <= 768
		},
		hasValidTarget() {
			return !!this.currentTargetElement
		},
	},
	mounted() {
		if (this.autoStart) {
			this.startTour()
		}
	},
	beforeUnmount() {
		this.unlockPageScroll()
		this.detachGlobalHandlers()
	},
	methods: {
		startTour() {
			if (!Array.isArray(this.steps) || this.steps.length === 0) return
			this.isActive = true
			this.currentStepIndex = 0
			this.lockPageScroll()
			this.attachGlobalHandlers()
			this.$nextTick(() => {
				this.updatePositions()
			})
		},

		nextStep() {
			if (this.currentStepIndex >= this.steps.length - 1) return
			this.currentStepIndex += 1
			this.$nextTick(() => {
				this.scrollToCurrentStepTarget()
				this.updatePositions()
			})
		},

		previousStep() {
			if (this.currentStepIndex <= 0) return
			this.currentStepIndex -= 1
			this.$nextTick(() => {
				this.scrollToCurrentStepTarget()
				this.updatePositions()
			})
		},

		skipTour() {
			this.finish('skip')
		},

		completeTour() {
			this.finish('complete')
		},

		restartTour() {
			if (!Array.isArray(this.steps) || this.steps.length === 0) return
			this.currentStepIndex = 0
			this.$nextTick(() => {
				this.scrollToCurrentStepTarget()
				this.updatePositions()
			})
		},

		finish(eventName) {
			this.isActive = false
			this.unlockPageScroll()
			this.detachGlobalHandlers()
			this.$emit(eventName)
		},

		lockPageScroll() {
			if (typeof document === 'undefined') return
			const html = document.documentElement
			const body = document.body
			this.previousHtmlOverflow = html.style.overflow || ''
			this.previousBodyOverflow = body.style.overflow || ''
			html.style.overflow = 'hidden'
			body.style.overflow = 'hidden'
		},

		unlockPageScroll() {
			if (typeof document === 'undefined') return
			const html = document.documentElement
			const body = document.body
			html.style.overflow = this.previousHtmlOverflow || ''
			body.style.overflow = this.previousBodyOverflow || ''
		},

		attachGlobalHandlers() {
			if (typeof window === 'undefined') return
			this.resizeHandler = () => this.updatePositions()
			window.addEventListener('resize', this.resizeHandler)
			window.addEventListener('scroll', this.resizeHandler, true)
			this.preventEscHandler = event => {
				if (event.key === 'Escape' && this.isActive) {
					event.preventDefault()
					event.stopPropagation()
				}
			}
			window.addEventListener('keydown', this.preventEscHandler, true)
		},

		detachGlobalHandlers() {
			if (typeof window === 'undefined') return
			if (this.resizeHandler) {
				window.removeEventListener('resize', this.resizeHandler)
				window.removeEventListener('scroll', this.resizeHandler, true)
				this.resizeHandler = null
			}
			if (this.preventEscHandler) {
				window.removeEventListener('keydown', this.preventEscHandler, true)
				this.preventEscHandler = null
			}
		},

		resolveTargetElement(step) {
			const selectors = []
			if (step?.target) selectors.push(step.target)
			if (Array.isArray(step?.fallbackTargets)) {
				selectors.push(...step.fallbackTargets)
			}
			for (const selector of selectors) {
				const node = document.querySelector(selector)
				if (node) return node
			}
			return null
		},

		scrollToCurrentStepTarget() {
			const step = this.currentStep
			if (!step) return
			const targetElement = this.resolveTargetElement(step)
			if (!targetElement) return
			const allowedPositions = ['start', 'center', 'end', 'nearest']
			const block = allowedPositions.includes(step?.scrollBlock)
				? step.scrollBlock
				: 'center'
			const inline = allowedPositions.includes(step?.scrollInline)
				? step.scrollInline
				: 'nearest'

			targetElement.scrollIntoView({
				behavior: 'smooth',
				block,
				inline,
			})
		},

		getBodyZoom() {
			if (typeof window === 'undefined') return 1
			const rawZoom = window.getComputedStyle(document.body).zoom
			const zoom = Number.parseFloat(rawZoom)
			if (!Number.isFinite(zoom) || zoom <= 0) return 1
			return zoom
		},

		updatePositions() {
			const step = this.currentStep
			if (!step) return

			const targetElement = this.resolveTargetElement(step)
			this.currentTargetElement = targetElement

			if (this.isMobile || !targetElement) {
				this.tooltipStyle = {}
				this.spotlightStyle = {}
				return
			}

			const zoom = this.getBodyZoom()
			const rect = targetElement.getBoundingClientRect()
			const normalizedRect = {
				top: rect.top / zoom,
				left: rect.left / zoom,
				right: rect.right / zoom,
				bottom: rect.bottom / zoom,
				width: rect.width / zoom,
				height: rect.height / zoom,
			}
			const basePadding = Number.isFinite(step?.spotlightPadding)
				? step.spotlightPadding
				: 0
			const padding = basePadding / zoom
			this.spotlightStyle = {
				top: `${normalizedRect.top - padding}px`,
				left: `${normalizedRect.left - padding}px`,
				width: `${Math.max(1, normalizedRect.width + padding * 2)}px`,
				height: `${Math.max(1, normalizedRect.height + padding * 2)}px`,
			}

			const tooltipWidth = Number.isFinite(step?.tooltipWidth)
				? step.tooltipWidth
				: 520
			const tooltipHeight = Number.isFinite(step?.tooltipHeight)
				? step.tooltipHeight
				: step?.hintImage
					? 680
					: 240
			const offset = 18 / zoom
			const shiftX = Number.isFinite(step?.tooltipShiftX)
				? step.tooltipShiftX / zoom
				: 0
			const shiftY = Number.isFinite(step?.tooltipShiftY)
				? step.tooltipShiftY / zoom
				: 0
			const fixedRight = Number.isFinite(step?.tooltipRight)
				? step.tooltipRight / zoom
				: null
			const shouldClampTooltip = step?.clampTooltip !== false
			const position = step.position || 'bottom'

			let top = normalizedRect.bottom + offset
			let left = normalizedRect.left + normalizedRect.width / 2 - tooltipWidth / 2

			if (position === 'top') {
				top = normalizedRect.top - tooltipHeight - offset
			} else if (position === 'left') {
				top = normalizedRect.top + normalizedRect.height / 2 - tooltipHeight / 2
				left = normalizedRect.left - tooltipWidth - offset
			} else if (position === 'right') {
				top = normalizedRect.top + normalizedRect.height / 2 - tooltipHeight / 2
				left = normalizedRect.right + offset
			}

			left += shiftX
			top += shiftY
			if (shouldClampTooltip) {
				const viewportWidth = window.innerWidth / zoom
				const viewportHeight = window.innerHeight / zoom
				const viewportPadding = 12 / zoom
				if (fixedRight === null) {
					left = Math.max(
						viewportPadding,
						Math.min(left, viewportWidth - tooltipWidth - viewportPadding)
					)
				}
				top = Math.max(
					viewportPadding,
					Math.min(top, viewportHeight - tooltipHeight - viewportPadding)
				)
			}

			const nextTooltipStyle = {
				top: `${top}px`,
				width: `${tooltipWidth}px`,
			}
			if (fixedRight !== null) {
				nextTooltipStyle.right = `${fixedRight}px`
			} else {
				nextTooltipStyle.left = `${left}px`
			}
			this.tooltipStyle = nextTooltipStyle
		},
	},
}
</script>

<style scoped>
.onboarding {
	position: fixed;
	inset: 0;
	z-index: 2147483647;
}

.onboarding__overlay {
	position: fixed;
	inset: 0;
	z-index: 0;
	background: rgba(15, 23, 42, 0.884);
	animation: onboardingFade 0.28s ease;
}

.onboarding__overlay--with-spotlight {
	background: transparent;
}

.onboarding__spotlight {
	position: fixed;
	z-index: 1;
	box-sizing: border-box;
	border-radius: 12px;
	border: 2px solid rgba(191, 219, 254, 0.95);
	box-shadow: 0 0 0 9999px rgba(15, 23, 42, 0.72), 0 8px 30px rgba(147, 197, 253, 0.2);
	pointer-events: none;
	transition: top 0.28s ease, left 0.28s ease, width 0.28s ease, height 0.28s ease;
}

.onboarding__tooltip {
	position: fixed;
	z-index: 2;
	width: 520px;
	max-width: calc(100vw - 24px);
	background: #ffffff;
	border-radius: 14px;
	padding: 18px 18px 14px;
	box-shadow: 0 20px 42px rgba(15, 23, 42, 0.3);
	animation: onboardingCardIn 0.28s ease;
}

.onboarding__tooltip-close {
	width: 32px;
	height: 32px;
	border: none;
	border-radius: 0;
	background: transparent;
	color: #475569;
	font-size: 30px;
	font-weight: 500;
	line-height: 1;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	transition: color 0.2s ease, transform 0.2s ease;
	flex-shrink: 0;
	padding: 0;
}

.onboarding__tooltip-close:hover {
	color: #0f172a;
	transform: scale(1.08);
}

.onboarding__tooltip-close:focus-visible {
	outline: none;
	box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.25);
}

.onboarding__tooltip--mobile {
	left: 12px !important;
	right: 12px;
	top: auto !important;
	bottom: 12px;
	width: auto;
}

.onboarding__tooltip-header {
	display: flex;
	justify-content: space-between;
	gap: 12px;
	align-items: flex-start;
}

.onboarding__tooltip-header-controls {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	flex-shrink: 0;
}

.onboarding__tooltip-title {
	margin: 0;
	font-size: 18px;
	line-height: 1.35;
	color: #0f172a;
}

.onboarding__tooltip-step {
	flex-shrink: 0;
	font-size: 12px;
	font-weight: 600;
	color: #64748b;
	background: #f1f5f9;
	border-radius: 999px;
	padding: 4px 8px;
}

.onboarding__tooltip-text {
	margin: 10px 0 14px;
	font-size: 14px;
	line-height: 1.55;
	color: #334155;
}

.onboarding__tooltip-image-wrap {
	margin: 0 0 14px;
	display: flex;
	justify-content: center;
}

.onboarding__tooltip-image {
	display: block;
	width: 100%;
	max-width: 480px;
	max-height: 420px;
	object-fit: contain;
	border-radius: 10px;
	border: 1px solid #e2e8f0;
	background: #f8fafc;
}

.onboarding__tooltip-footer {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 10px;
}

.onboarding__tooltip-progress {
	display: flex;
	gap: 6px;
}

.onboarding__tooltip-dot {
	width: 0px;
	height: 0px;
	border-radius: 50%;
	background: #cbd5e1;
	transition: all 0.2s ease;
}

.onboarding__tooltip-dot--active {
	width: 22px;
	border-radius: 999px;
	background: #2563eb;
}

.onboarding__tooltip-actions {
	display: flex;
	flex-wrap: wrap;
	justify-content: flex-end;
	gap: 8px;
}

.onboarding__tooltip-btn {
	height: 38px;
	padding: 0 14px;
	border-radius: 10px;
	border: 1px solid transparent;
	font-size: 13px;
	font-weight: 600;
	line-height: 1;
	cursor: pointer;
	transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease,
		box-shadow 0.2s ease, transform 0.2s ease;
}

.onboarding__tooltip-btn:hover {
	transform: translateY(-1px);
}

.onboarding__tooltip-btn:active {
	transform: translateY(0);
}

.onboarding__tooltip-btn:focus-visible {
	outline: none;
	box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.25);
}

.onboarding__tooltip-btn--ghost {
	background: #f8fafc;
	border-color: #e2e8f0;
	color: #475569;
}

.onboarding__tooltip-btn--ghost:hover {
	background: #f1f5f9;
	border-color: #cbd5e1;
	color: #334155;
}

.onboarding__tooltip-btn--secondary {
	border-color: #cbd5e1;
	background: #ffffff;
	color: #0f172a;
}

.onboarding__tooltip-btn--secondary:hover {
	background: #f8fafc;
	border-color: #94a3b8;
}

.onboarding__tooltip-btn--primary {
	border-color: #1d4ed8;
	background: #2563eb;
	color: #ffffff;
	box-shadow: 0 8px 20px rgba(37, 99, 235, 0.22);
}

.onboarding__tooltip-btn--primary:hover {
	background: #1d4ed8;
	border-color: #1e40af;
	box-shadow: 0 10px 24px rgba(29, 78, 216, 0.28);
}

@keyframes onboardingFade {
	from {
		opacity: 0;
	}
	to {
		opacity: 1;
	}
}

@keyframes onboardingCardIn {
	from {
		opacity: 0;
		transform: translateY(8px) scale(0.985);
	}
	to {
		opacity: 1;
		transform: translateY(0) scale(1);
	}
}

@media (max-width: 768px) {
	.onboarding__tooltip-footer {
		flex-direction: column;
		align-items: stretch;
	}

	.onboarding__tooltip-progress {
		justify-content: center;
	}

	.onboarding__tooltip-actions {
		justify-content: stretch;
	}

	.onboarding__tooltip-btn {
		flex: 1 1 auto;
	}
}
</style>




